import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Button,
    Link,
    Stack,
    IconButton,
    TextField,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { authService } from '../services/authService';
import styles from '../components/Login/LoginPage.module.css';
import logo from '../assets/images/logo/logo.png';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username || !password) {
            setError('Please enter both username and password.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await authService.login({ username, password });
            navigate('/');
        } catch (err: any) {
            setError(err.message || 'Failed to login. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.loginBg}>
            <Paper elevation={3} className={styles.loginCard}>
                <Stack spacing={isMobile ? 2 : 3} alignItems='center'>
                    <img
                        src={logo}
                        alt='Gensize Logo'
                        style={{
                            width: isMobile ? '120px' : '150px',
                        }}
                    />
                    <Typography variant='h5' className={styles.heading}>
                        Sign in to Your Account
                    </Typography>
                    <form onSubmit={handleLogin} className={styles.form} autoComplete='off'>
                        <Stack spacing={isMobile ? 1.5 : 2}>
                            <TextField
                                label='Username'
                                value={username}
                                onChange={e => {
                                    setUsername(e.target.value);
                                    if (error) setError('');
                                }}
                                fullWidth
                                required
                                autoFocus
                                variant='outlined'
                                size={isMobile ? 'small' : 'medium'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '4px',
                                    },
                                }}
                                slots={{
                                    input: undefined,
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <PersonOutlineIcon
                                                    sx={{ fontSize: isMobile ? 20 : 24 }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <TextField
                                label='Password'
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={e => {
                                    setPassword(e.target.value);
                                    if (error) setError('');
                                }}
                                fullWidth
                                required
                                variant='outlined'
                                size={isMobile ? 'small' : 'medium'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '4px',
                                    },
                                }}
                                slots={{
                                    input: undefined,
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <LockOutlinedIcon
                                                    sx={{ fontSize: isMobile ? 20 : 24 }}
                                                />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <IconButton
                                                    aria-label={
                                                        showPassword
                                                            ? 'Hide password'
                                                            : 'Show password'
                                                    }
                                                    onClick={() => setShowPassword(prev => !prev)}
                                                    edge='end'
                                                    size={isMobile ? 'small' : 'medium'}
                                                    tabIndex={-1}
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff
                                                            sx={{ fontSize: isMobile ? 20 : 24 }}
                                                        />
                                                    ) : (
                                                        <Visibility
                                                            sx={{ fontSize: isMobile ? 20 : 24 }}
                                                        />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <div style={{ textAlign: 'right' }}>
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => navigate('/request-password')}
                                    underline='hover'
                                    color='#49454f'
                                    sx={{
                                        fontSize: isMobile ? 11 : 12,
                                        fontFamily: 'Poppins, sans-serif',
                                        border: 'none',
                                        background: 'none',
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                >
                                    Request New Password?
                                </Link>
                            </div>
                            {error && (
                                <Alert 
                                    severity="error" 
                                    sx={{ 
                                        mt: 1,
                                        '& .MuiAlert-message': {
                                            fontSize: isMobile ? '12px' : '14px',
                                            fontFamily: 'Poppins, sans-serif',
                                        }
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}
                            <Button
                                type='submit'
                                variant='contained'
                                fullWidth
                                className={styles.loginButton}
                                disabled={loading}
                                sx={{ mt: isMobile ? 1 : 2, color: 'white' }}
                            >
                                {loading ? 'Signing in...' : 'Proceed Next'}
                            </Button>
                        </Stack>
                    </form>
                </Stack>
            </Paper>
        </div>
    );
};

export default LoginPage;
